// @ts-nocheck
import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Breadcrumb,
  BreadcrumbItem,
  Form,
  FormGroup,
  TextInput,
  Button,
  InlineNotification,
  ProgressIndicator,
  ProgressStep,
  RadioButtonGroup,
  RadioButton,
  Checkbox,
  Modal,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Tag,
  Stack,
} from '@carbon/react';
import { UserAvatar, CheckmarkFilled, WarningFilled, Document, Security, Purchase, Email } from '@carbon/icons-react';
import { Link, useParams } from 'react-router-dom';
import { AuthGuard } from '../../components/auth/AuthGuard';
import { privacyService } from '../../services/privacyService';
import { useAuth } from '../../hooks/useAuth';

interface ChildData {
  id: string;
  name: string;
  email: string;
  age: number;
  registeredAt: string;
  consentStatus: 'pending' | 'granted' | 'denied';
  requestedConsents: string[];
}

export const ParentConsentPage: React.FC = () => {
  const { childId } = useParams<{ childId: string }>();
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(0);
  const [childData, setChildData] = useState<ChildData | null>(null);
  const [verificationMethod, setVerificationMethod] = useState<'email' | 'creditcard' | 'id'>('email');
  const [verificationData, setVerificationData] = useState({
    email: '',
    cardNumber: '',
    cardExpiry: '',
    cardCvc: '',
    idNumber: '',
    idType: 'drivers_license',
  });
  const [consentsToGrant, setConsentsToGrant] = useState<string[]>([]);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [notification, setNotification] = useState<{
    type: 'success' | 'error' | 'warning' | 'info';
    title: string;
    subtitle?: string;
  } | null>(null);

  const consentTypes = [
    {
      id: 'educational',
      name: 'Educational Records',
      description:
        'Allow collection and processing of educational data including grades, assignments, and progress tracking',
      required: true,
    },
    {
      id: 'analytics',
      name: 'Analytics & Improvement',
      description: 'Help improve the platform through anonymous usage analytics',
      required: false,
    },
    {
      id: 'personalization',
      name: 'Personalized Learning',
      description: 'Enable AI-powered learning recommendations and adaptive content',
      required: false,
    },
    {
      id: 'communication',
      name: 'Educational Communications',
      description: "Receive important updates about your child's education",
      required: false,
    },
    {
      id: 'thirdparty',
      name: 'Educational Partner Sharing',
      description: 'Share data with approved educational institutions and assessment providers',
      required: false,
    },
  ];

  useEffect(() => {
    if (childId) {
      loadChildData();
    }
  }, [childId]);

  const loadChildData = async () => {
    try {
      // Mock data for demonstration
      setChildData({
        id: childId!,
        name: 'Alex Johnson',
        email: 'alex.johnson@school.edu',
        age: 11,
        registeredAt: new Date().toISOString(),
        consentStatus: 'pending',
        requestedConsents: ['educational', 'analytics', 'personalization'],
      });

      // Pre-select required consents
      setConsentsToGrant(['educational']);
    } catch (error) {
      console.error('Failed to load child data:', error);
      setNotification({
        type: 'error',
        title: 'Failed to load consent request',
        subtitle: 'Please check the link and try again',
      });
    }
  };

  const handleVerificationSubmit = async () => {
    setLoading(true);
    try {
      // Validate verification data
      if (verificationMethod === 'email' && !verificationData.email) {
        throw new Error('Email address is required');
      }
      if (
        verificationMethod === 'creditcard' &&
        (!verificationData.cardNumber || !verificationData.cardExpiry || !verificationData.cardCvc)
      ) {
        throw new Error('Complete credit card information is required');
      }
      if (verificationMethod === 'id' && !verificationData.idNumber) {
        throw new Error('ID number is required');
      }

      // Submit verification
      await privacyService.verifyParentalConsent(childId!, verificationMethod);

      setCurrentStep(1);
      setNotification({
        type: 'success',
        title: 'Identity verified successfully',
        subtitle: 'Please review and select the consents to grant',
      });
    } catch (error: any) {
      console.error('Verification failed:', error);
      setNotification({
        type: 'error',
        title: 'Verification failed',
        subtitle: error.message || 'Please check your information and try again',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConsentSubmit = async () => {
    if (!agreedToTerms) {
      setNotification({
        type: 'warning',
        title: 'Terms agreement required',
        subtitle: 'Please agree to the terms and conditions',
      });
      return;
    }

    setLoading(true);
    try {
      // Submit consents
      for (const consentId of consentsToGrant) {
        await privacyService.recordConsent(childId!, {
          type: consentId,
          granted: true,
          purpose: consentTypes.find(c => c.id === consentId)?.description || '',
          dataCategories: [],
          withdrawable: true,
          parentConsentRequired: true,
          version: '1.0.0',
        });
      }

      setShowSuccessModal(true);
    } catch (error) {
      console.error('Failed to submit consent:', error);
      setNotification({
        type: 'error',
        title: 'Failed to submit consent',
        subtitle: 'Please try again or contact support',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleConsentToggle = (consentId: string, checked: boolean) => {
    if (checked) {
      setConsentsToGrant([...consentsToGrant, consentId]);
    } else {
      // Don't allow unchecking required consents
      const consent = consentTypes.find(c => c.id === consentId);
      if (!consent?.required) {
        setConsentsToGrant(consentsToGrant.filter(id => id !== consentId));
      }
    }
  };

  if (!childData) {
    return (
      <Grid fullWidth>
        <Column lg={16} md={8} sm={4}>
          <InlineNotification
            kind="error"
            title="Invalid consent request"
            subtitle="The consent request link appears to be invalid or expired"
          />
        </Column>
      </Grid>
    );
  }

  return (
    <Grid fullWidth>
      <Column lg={16} md={8} sm={4}>
        <Breadcrumb>
          <BreadcrumbItem>
            <Link to="/">Home</Link>
          </BreadcrumbItem>
          <BreadcrumbItem>
            <Link to="/privacy">Privacy</Link>
          </BreadcrumbItem>
          <BreadcrumbItem isCurrentPage>Parental Consent</BreadcrumbItem>
        </Breadcrumb>

        <div className="page-header">
          <h1>Parental Consent for Child Account</h1>
          <p>COPPA requires verifiable parental consent for users under 13</p>
        </div>

        {notification && (
          <InlineNotification
            kind={notification.type}
            title={notification.title}
            subtitle={notification.subtitle}
            onClose={() => setNotification(null)}
            lowContrast
          />
        )}

        <ProgressIndicator currentIndex={currentStep} spaceEqually>
          <ProgressStep label="Verify Identity" description="Confirm you are the parent/guardian" />
          <ProgressStep label="Review Consents" description="Select data processing permissions" />
          <ProgressStep label="Confirmation" description="Complete the consent process" />
        </ProgressIndicator>

        <div className="consent-content">
          {currentStep === 0 && (
            <Stack gap={6}>
              <div className="child-info">
                <h3>Child Information</h3>
                <Tag kind="blue" renderIcon={UserAvatar}>
                  Under 13
                </Tag>
                <p>
                  <strong>Name:</strong> {childData.name}
                </p>
                <p>
                  <strong>Email:</strong> {childData.email}
                </p>
                <p>
                  <strong>Age:</strong> {childData.age} years old
                </p>
                <p>
                  <strong>Registration Date:</strong> {new Date(childData.registeredAt).toLocaleDateString()}
                </p>
              </div>

              <Form
                onSubmit={e => {
                  e.preventDefault();
                  handleVerificationSubmit();
                }}
              >
                <FormGroup legendText="Parent/Guardian Verification">
                  <RadioButtonGroup
                    legendText="Select verification method"
                    name="verification-method"
                    defaultSelected="email"
                    onChange={value => setVerificationMethod(value as any)}
                  >
                    <RadioButton
                      id="verify-email"
                      labelText="Email verification (Free)"
                      value="email"
                      renderIcon={Email}
                    />
                    <RadioButton
                      id="verify-creditcard"
                      labelText="Credit card verification ($0.50 charge)"
                      value="creditcard"
                      renderIcon={Purchase}
                    />
                    <RadioButton
                      id="verify-id"
                      labelText="Government ID verification"
                      value="id"
                      renderIcon={Document}
                    />
                  </RadioButtonGroup>

                  {verificationMethod === 'email' && (
                    <TextInput
                      id="parent-email"
                      labelText="Your email address"
                      placeholder="parent@example.com"
                      value={verificationData.email}
                      onChange={e =>
                        setVerificationData({
                          ...verificationData,
                          email: e.target.value,
                        })
                      }
                      required
                    />
                  )}

                  {verificationMethod === 'creditcard' && (
                    <>
                      <TextInput
                        id="card-number"
                        labelText="Card number"
                        placeholder="1234 5678 9012 3456"
                        value={verificationData.cardNumber}
                        onChange={e =>
                          setVerificationData({
                            ...verificationData,
                            cardNumber: e.target.value,
                          })
                        }
                        required
                      />
                      <Grid narrow>
                        <Column lg={8}>
                          <TextInput
                            id="card-expiry"
                            labelText="Expiry date"
                            placeholder="MM/YY"
                            value={verificationData.cardExpiry}
                            onChange={e =>
                              setVerificationData({
                                ...verificationData,
                                cardExpiry: e.target.value,
                              })
                            }
                            required
                          />
                        </Column>
                        <Column lg={8}>
                          <TextInput
                            id="card-cvc"
                            labelText="CVC"
                            placeholder="123"
                            value={verificationData.cardCvc}
                            onChange={e =>
                              setVerificationData({
                                ...verificationData,
                                cardCvc: e.target.value,
                              })
                            }
                            required
                          />
                        </Column>
                      </Grid>
                      <InlineNotification
                        kind="info"
                        title="$0.50 verification charge"
                        subtitle="A temporary charge of $0.50 will be placed on your card for verification. This will be refunded immediately."
                        hideCloseButton
                        lowContrast
                      />
                    </>
                  )}

                  {verificationMethod === 'id' && (
                    <>
                      <RadioButtonGroup
                        legendText="ID type"
                        name="id-type"
                        defaultSelected="drivers_license"
                        onChange={value =>
                          setVerificationData({
                            ...verificationData,
                            idType: value,
                          })
                        }
                      >
                        <RadioButton id="drivers-license" labelText="Driver's License" value="drivers_license" />
                        <RadioButton id="passport" labelText="Passport" value="passport" />
                        <RadioButton id="state-id" labelText="State ID" value="state_id" />
                      </RadioButtonGroup>
                      <TextInput
                        id="id-number"
                        labelText="ID number"
                        placeholder="Enter your ID number"
                        value={verificationData.idNumber}
                        onChange={e =>
                          setVerificationData({
                            ...verificationData,
                            idNumber: e.target.value,
                          })
                        }
                        required
                      />
                    </>
                  )}

                  <Button type="submit" disabled={loading}>
                    {loading ? 'Verifying...' : 'Verify Identity'}
                  </Button>
                </FormGroup>
              </Form>
            </Stack>
          )}

          {currentStep === 1 && (
            <Stack gap={6}>
              <div className="consent-review">
                <h3>Review and Grant Consents</h3>
                <p>Please review the following data processing activities and select which ones you consent to:</p>

                <Form
                  onSubmit={e => {
                    e.preventDefault();
                    handleConsentSubmit();
                  }}
                >
                  <FormGroup legendText="Data Processing Consents">
                    {consentTypes.map(consent => (
                      <Checkbox
                        key={consent.id}
                        id={`consent-${consent.id}`}
                        labelText={
                          <div>
                            <strong>{consent.name}</strong>
                            {consent.required && (
                              <Tag kind="blue" size="sm">
                                Required
                              </Tag>
                            )}
                            <p>{consent.description}</p>
                          </div>
                        }
                        checked={consentsToGrant.includes(consent.id)}
                        onChange={(_, { checked }) => handleConsentToggle(consent.id, checked)}
                        disabled={consent.required}
                      />
                    ))}
                  </FormGroup>

                  <FormGroup legendText="Terms and Conditions">
                    <Checkbox
                      id="agree-terms"
                      labelText="I confirm that I am the parent or legal guardian of this child and have the authority to provide consent for data processing. I have read and agree to the Privacy Policy and Terms of Service."
                      checked={agreedToTerms}
                      onChange={(_, { checked }) => setAgreedToTerms(checked)}
                    />
                  </FormGroup>

                  <div className="form-actions">
                    <Button kind="secondary" onClick={() => setCurrentStep(0)}>
                      Back
                    </Button>
                    <Button type="submit" disabled={loading || !agreedToTerms || consentsToGrant.length === 0}>
                      {loading ? 'Submitting...' : 'Grant Consent'}
                    </Button>
                  </div>
                </Form>
              </div>
            </Stack>
          )}
        </div>

        {/* Success Modal */}
        <Modal
          open={showSuccessModal}
          modalHeading="Consent Granted Successfully"
          modalLabel="COPPA Compliance"
          primaryButtonText="Done"
          onRequestSubmit={() => (window.location.href = '/')}
          passiveModal
        >
          <Stack gap={5}>
            <CheckmarkFilled size={48} style={{ color: 'var(--cds-support-success)' }} />
            <p>
              You have successfully granted consent for your child's account. They can now access the educational
              features of our platform.
            </p>
            <div className="consent-summary">
              <h4>Consents Granted:</h4>
              <ul>
                {consentsToGrant.map(consentId => {
                  const consent = consentTypes.find(c => c.id === consentId);
                  return consent ? <li key={consentId}>{consent.name}</li> : null;
                })}
              </ul>
            </div>
            <p>
              You can manage these consents at any time from your parent dashboard. You will receive a confirmation
              email with details about data processing and your rights under COPPA.
            </p>
          </Stack>
        </Modal>
      </Column>
    </Grid>
  );
};
